import type { BreadthPoint, MinuteBucket, StockPoint } from "../lib/types";

type LineSpec<T> = {
  key: keyof T;
  color: string;
};

function scale(values: number[], height: number): (value: number) => number {
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  return (value: number) => height - ((value - min) / range) * height;
}

export function MultiLineChart({ data }: { data: BreadthPoint[] }) {
  const width = 760;
  const height = 260;
  const lines: LineSpec<BreadthPoint>[] = [
    { key: "advancing", color: "#5ce16d" },
    { key: "declining", color: "#ff5f57" },
    { key: "newHighs", color: "#f6d84a" },
    { key: "newLows", color: "#9aa7b2" },
    { key: "spyProxy", color: "#5b8cff" }
  ];
  if (!data.length) return <div className="empty">No breadth data</div>;
  const all = lines.flatMap((line) => data.map((item) => Number(item[line.key]) || 0));
  const y = scale(all, height);
  const step = width / Math.max(1, data.length - 1);

  return (
    <svg className="chart" viewBox={`0 0 ${width} ${height + 36}`} role="img" aria-label="Market breadth minute chart">
      {[0, 1, 2, 3].map((line) => (
        <line key={line} x1="0" x2={width} y1={(height / 4) * line} y2={(height / 4) * line} className="grid-line" />
      ))}
      {lines.map((line) => {
        const points = data.map((item, index) => `${index * step},${y(Number(item[line.key]) || 0)}`).join(" ");
        return <polyline key={String(line.key)} points={points} fill="none" stroke={line.color} strokeWidth="2.4" />;
      })}
      {data.filter((_, index) => index % 60 === 0).map((item, index) => (
        <text key={item.time} x={index * 60 * step} y={height + 24} className="axis-label">
          {item.time}
        </text>
      ))}
    </svg>
  );
}

export function VolumeBars({ data }: { data: MinuteBucket[] }) {
  const width = 420;
  const height = 190;
  if (!data.length) return <div className="empty">No minute volume data</div>;
  const max = Math.max(...data.map((item) => item.volume)) || 1;
  const barWidth = width / data.length;
  return (
    <svg className="chart" viewBox={`0 0 ${width} ${height + 28}`} role="img" aria-label="Volume at minute histogram">
      {data.map((item, index) => {
        const barHeight = (item.volume / max) * height;
        return (
          <rect
            key={`${item.time}-${index}`}
            x={index * barWidth}
            y={height - barHeight}
            width={Math.max(1, barWidth - 1)}
            height={barHeight}
            rx="1"
            fill={index % 5 === 0 ? "#86a8ff" : "#5f7dbb"}
          />
        );
      })}
      {data.filter((_, index) => index % 75 === 0).map((item, index) => (
        <text key={item.time} x={index * 75 * barWidth} y={height + 22} className="axis-label">
          {item.time}
        </text>
      ))}
    </svg>
  );
}

export function Sparkline({
  points,
  selectedIndex,
  onSelect
}: {
  points: StockPoint[];
  selectedIndex?: number | null;
  onSelect?: (index: number) => void;
}) {
  const width = 340;
  const height = 118;
  if (!points.length) return <div className="empty small">No intraday trace</div>;
  const y = scale(points.map((item) => item.close), height);
  const step = width / Math.max(1, points.length - 1);
  const line = points.map((item, index) => `${index * step},${y(item.close)}`).join(" ");
  const activeIndex = Math.min(points.length - 1, Math.max(0, selectedIndex ?? points.length - 1));
  const activePoint = points[activeIndex];
  const activeX = activeIndex * step;
  const activeY = y(activePoint.close);
  const hitWidth = Math.max(8, step);
  return (
    <svg className="sparkline" viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Selected ticker intraday sparkline">
      <polyline points={line} fill="none" stroke="#4cff75" strokeWidth="2" />
      <line x1={activeX} x2={activeX} y1="0" y2={height} className="crosshair-line" />
      <circle cx={activeX} cy={activeY} r="4.2" className="crosshair-dot" />
      {onSelect
        ? points.map((item, index) => (
            <rect
              key={`${item.time}-${index}`}
              className="spark-hit"
              x={Math.max(0, index * step - hitWidth / 2)}
              y="0"
              width={hitWidth}
              height={height}
              onClick={() => onSelect(index)}
            />
          ))
        : null}
    </svg>
  );
}

export function MinuteHeatmap({ data }: { data: MinuteBucket[] }) {
  const sample = data.filter((_, index) => index % 20 === 0).slice(0, 72);
  if (!sample.length) return <div className="empty">No heatmap data</div>;
  const max = Math.max(...sample.map((item) => item.volume)) || 1;
  return (
    <div className="heatmap" aria-label="Minute volume heatmap">
      {sample.map((item) => {
        const intensity = Math.max(0.12, item.volume / max);
        return (
          <div key={item.time} className="heat-cell" style={{ opacity: 0.45 + intensity * 0.55 }}>
            <span>{item.time}</span>
            <strong>{Math.round(intensity * 100)}%</strong>
          </div>
        );
      })}
    </div>
  );
}
